import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useNavigate, useNavigationType, useSearchParams } from 'react-router'

import { useNotifications } from 'app/application/application'
import { useAuthorization } from 'app/application/presentation'
import { AuthorizedRoutes, PublicRoutes, SettingsRoutes } from 'app/application/types'
import { useUser, useWorkspace } from 'app/user/application'
import { NewUserInviteContent, WorkspaceCreateContent } from 'app/user/types'

import { AccessPage } from '../AccessPage'

interface CreateAccessProps {
  isCreateWorkspace?: boolean
}

type UserAccessContent = WorkspaceCreateContent | NewUserInviteContent

type FormInput = {
  passwordRepeat: string
} & UserAccessContent

// eslint-disable-next-line react-refresh/only-export-components
export const isCreateWorkspaceInput = (input: UserAccessContent): input is WorkspaceCreateContent =>
  (input as WorkspaceCreateContent).workspace != undefined

export interface UserAccessContext {
  error: string
  formInput: FormInput
  handleCreateWorkspace: () => void
  loggedIn: boolean | undefined
  setError: Dispatch<SetStateAction<string>>
  setFormInput: Dispatch<SetStateAction<FormInput>>
}

const settingsRoute = `/${AuthorizedRoutes.Settings}/${SettingsRoutes.Profile}`

const CreateAccess = ({ isCreateWorkspace }: CreateAccessProps) => {
  const navigate = useNavigate()
  const searchParams = useSearchParams()[0]

  const { createWorkspace, getInvite } = useWorkspace()
  const { setAuthorization } = useAuthorization()
  const { createNotification, te } = useNotifications()
  const { t } = useTranslation('user')
  const { changeWorkspace, handleInvite, login, logout } = useUser()

  const [formInput, setFormInput] = useState<FormInput>({
    firstName: '',
    email: searchParams.get('email') ?? '',
    lastName: '',
    inviteCode: searchParams.get('inviteCode') ?? '',
    password: '',
    passwordRepeat: '',
    workspace: isCreateWorkspace ? '' : undefined,
  })
  const [error, setError] = useState('')
  const [loggedIn, setLoggedIn] = useState<boolean>()

  const navigationType = useNavigationType()

  useEffect(() => {
    setError(
      !formInput.email || !formInput.inviteCode ? t('create.error.missingEMailOrInviteCode') : '',
    )
  }, [formInput.email, formInput.inviteCode, navigationType, t])

  useEffect(() => {
    if (formInput.email && formInput.inviteCode) {
      void getInvite(formInput.email, formInput.inviteCode, isCreateWorkspace ?? false).then(
        (result) => {
          if (result.ok) {
            if (
              result.val.loggedInUserId &&
              result.val.loggedInUserId !== result.val.user?.userId
            ) {
              void logout()
            } else if (result.val.status == 'active') {
              if (result.val.loggedInUserId) {
                if (isCreateWorkspace) {
                  const user = result.val.user
                  setFormInput((prev) => ({
                    ...prev,
                    firstName: user.firstName ?? '',
                    lastName: user.lastName ?? '',
                  }))
                  setLoggedIn(true)
                } else {
                  void navigate(settingsRoute)
                }
              } else {
                const createWorkspaceUrl = `${PublicRoutes.CreateWorkspace}?${new URLSearchParams({
                  email: formInput.email,
                  inviteCode: formInput.inviteCode,
                })}`
                setAuthorization(null)
                void navigate(PublicRoutes.Login, {
                  state: {
                    email: formInput.email,
                    redirect: isCreateWorkspace ? createWorkspaceUrl : settingsRoute,
                  },
                })
              }
            } else {
              setLoggedIn(false)
            }
          } else {
            setError(result.val.message)
          }
        },
      )
    }
  }, [
    formInput.email,
    formInput.inviteCode,
    getInvite,
    isCreateWorkspace,
    logout,
    navigate,
    setAuthorization,
  ])

  const loginUser = () =>
    login(formInput.email, formInput.password).then((loginResult) => {
      if (loginResult.ok) {
        void navigate('/')
      } else {
        createNotification({
          message: loginResult.val.message,
          title: te(`http.${loginResult.val.code}`),
          type: 'error',
        })
      }
    })

  const handleCreateWorkspace = () => {
    const { passwordRepeat: _, ...userAccessContent } = formInput
    if (isCreateWorkspaceInput(userAccessContent)) {
      void createWorkspace(userAccessContent).then((result) => {
        if (result.ok) {
          if (loggedIn) {
            void changeWorkspace(result.val, async (success) => {
              if (success) {
                await navigate('/')
              }
            })
          } else {
            void loginUser()
          }
        } else {
          setError(result.val.message)
        }
      })
    } else {
      void handleInvite('accept', userAccessContent).then((result) => {
        if (result.ok) {
          if (!loggedIn) {
            void loginUser()
          } else {
            // should not be possible, as existing user invite is done in user settings page
          }
        } else {
          setError(result.val.message)
        }
      })
    }
  }

  return (
    <AccessPage>
      <Outlet
        context={{
          error,
          formInput,
          handleCreateWorkspace,
          loggedIn,
          setError,
          setFormInput,
        }}
      />
    </AccessPage>
  )
}

export default CreateAccess
